import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { CornerDownRight, LoaderCircle, MessageSquareText, Send, Trash2, X } from "lucide-react";
import type { MediaAsset, PostComment } from "../../shared/contracts";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { formatDateTime, initials } from "../lib/format";
import { toast } from "../lib/toast";
import { ImageUploader } from "./ImageUploader";
import { StatePanel } from "./StatePanel";

interface CommentsSectionProps {
  postId: string;
  shareToken?: string;
  readOnly?: boolean;
}

export function CommentsSection({ postId, shareToken, readOnly = false }: CommentsSectionProps) {
  const { user, requestLogin, refreshUnread } = useApp();
  const [items, setItems] = useState<PostComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [media, setMedia] = useState<MediaAsset[]>([]);
  const [replyingTo, setReplyingTo] = useState<PostComment | null>(null);
  const [busy, setBusy] = useState(false);
  const composerInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (window.location.hash !== "#comment-composer") return;
    const timer = window.setTimeout(() => {
      document.getElementById("comment-composer")?.scrollIntoView({
        block: "start",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
      });
      composerInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const path = shareToken ? `/posts/share/${shareToken}/comments` : `/posts/${postId}/comments`;
      setItems((await api<{ items: PostComment[] }>(path)).items);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "评论加载失败");
    } finally { setLoading(false); }
  }, [postId, shareToken]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const refresh = () => void load();
    window.addEventListener("share-moments:content.approved", refresh);
    return () => window.removeEventListener("share-moments:content.approved", refresh);
  }, [load]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) { requestLogin("请先登录账号，才可以发表评论。", window.location.pathname); return; }
    if (!text.trim() && !media[0]) return;
    setBusy(true); setError(null);
    try {
      const result = await api<{ comment: PostComment }>(`/posts/${postId}/comments`, {
        method: "POST",
        body: JSON.stringify({ text: text.trim() || null, mediaId: media[0]?.id ?? null, parentId: replyingTo?.id ?? null })
      });
      setItems((current) => [...current, result.comment]);
      setText(""); setMedia([]); setReplyingTo(null);
      toast(result.comment.reviewStatus === "pending" ? "评论图片审核通过后公开" : "评论已发布", "success");
      await refreshUnread();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "评论发布失败");
    } finally { setBusy(false); }
  };

  const remove = async (comment: PostComment) => {
    try {
      await api(`/posts/${postId}/comments/${comment.id}`, { method: "DELETE" });
      setItems((current) => current.map((item) => item.id === comment.id ? { ...item, text: null, media: null, deleted: true } : item));
      toast("评论已删除");
    } catch (removeError) { toast(removeError instanceof Error ? removeError.message : "评论删除失败", "danger"); }
  };

  return (
    <section id="comments-section" className="comments-section" aria-labelledby="comments-title">
      <header className="comments-heading"><div><MessageSquareText /><span><strong id="comments-title">评论与回复</strong><small>{items.filter((item) => !item.deleted).length} 条讨论</small></span></div></header>
      {loading ? <StatePanel kind="loading" title="正在读取评论" /> : null}
      {!loading && error ? <StatePanel kind="error" title="评论加载失败" description={error} onRetry={() => void load()} /> : null}
      {!loading && !error && items.length === 0 ? <StatePanel kind="empty" title="还没有评论" description={readOnly ? "这条分享暂时没有公开评论。" : "围绕动态内容友善交流，第一条评论由你开始。"} /> : null}
      <div className="comment-list">
        {items.map((comment) => <article className={`comment-item${comment.parentId ? " is-reply" : ""}`} key={comment.id}>
          <Avatar src={comment.author.avatarUrl ?? undefined}>{initials(comment.author.nickname)}</Avatar>
          <div className="comment-body"><header><span><strong>{comment.author.nickname}</strong>{comment.replyTo ? <small><CornerDownRight />回复 {comment.replyTo.nickname}</small> : null}</span><time>{formatDateTime(comment.createdAt)}</time></header>
            {comment.deleted ? <p className="comment-deleted">该评论已删除</p> : <>{comment.text ? <p>{comment.text}</p> : null}{comment.media ? <ButtonBase className="comment-image" type="button" aria-label="在新窗口查看评论图片" onClick={() => window.open(comment.media!.url, "_blank", "noopener,noreferrer")}><img src={comment.media.url} alt="评论图片" />{comment.reviewStatus === "pending" ? <span>审核中，仅自己可见</span> : null}</ButtonBase> : null}</>}
            {!readOnly && !comment.deleted ? <div className="comment-actions"><Button size="small" variant="text" type="button" startIcon={<CornerDownRight />} onClick={() => setReplyingTo(comment)}>回复</Button>{user && (user.id === comment.author.id || user.role === "admin") ? <Tooltip title="删除评论"><IconButton type="button" aria-label="删除评论" onClick={() => void remove(comment)}><Trash2 /></IconButton></Tooltip> : null}</div> : null}
          </div>
        </article>)}
      </div>
      {!readOnly ? <form id="comment-composer" className="comment-composer" onSubmit={(event) => void submit(event)}>
        {replyingTo ? <div className="replying-banner"><span>回复 {replyingTo.author.nickname}</span><IconButton type="button" aria-label="取消回复" onClick={() => setReplyingTo(null)}><X /></IconButton></div> : null}
        {error && !loading ? <Alert severity="error">{error}</Alert> : null}
        <TextField inputRef={composerInputRef} multiline minRows={3} maxRows={8} value={text} onChange={(event) => { setText(event.target.value); setError(null); }} placeholder="写下对这条动态的评论" helperText={`${text.length} / 1000`} slotProps={{ htmlInput: { maxLength: 1000 } }} />
        <div className="comment-composer-footer"><div className="comment-media-control"><ImageUploader context="comment" value={media} onChange={setMedia} max={1} compact /></div><Button variant="contained" type="submit" disabled={busy || (!text.trim() && !media[0])} startIcon={busy ? <LoaderCircle className="spin" /> : <Send />}>{busy ? "正在发布" : "发布评论"}</Button></div>
      </form> : null}
    </section>
  );
}
